import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiPatch, apiPost, apiPut } from '@/features/shared/utils/dataFetching'
import { v4 as uuidv4 } from 'uuid'
import {
  type UpdateOrderModParams,
  type UpdateOrderModResponse,
  type Feedback,
  type Order,
  type AviatorOrder,
  type Rating,
  type Tip,
} from '@/features/shop/services/Orders/types'
import { getOrderLinesForUpdateOrder } from '@/features/shop/services/Orders/utils'
import {
  useCurrentActiveOrderId,
  useClearActiveOrder,
} from '@/features/shop/state/SelectedActiveOrder/hooks'
import { getRequestedQuantityForProduct } from '@/features/shared/utils/normalizeProduct'
import { logError } from '@/features/shared/utils/logger'
import { AnalyticsEvent } from '@shipt/analytics-member-web'
import { track } from '@/features/shared/analytics/trackingQueue'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { refreshCoupons } from '@/features/shop/services/Coupons/utils'
import {
  UpdateOrderMutation,
  ActiveOrdersQuery,
  OrdersQuery,
} from '@/features/shop/services/Orders/queries'
import { orderQueryOptions } from '@/features/shop/services/Orders/queryOptions'
import { fetchReasonCode } from '@/features/shop/services/ReasonCodes/fetchers'
import { creditDetailsQueryOptions } from '@/features/purchase/services/Wallet/queries'
import { timeslotsQueryOptions } from '@/features/shop/services/DeliveryWindows/queries'
import { getErrorStatus } from '@/features/shared/utils/dataFetching/utils'
import { trackElementClicked } from '@/features/shared/analytics/element'
import {
  orderAlreadyProcessedIgnoredMessages,
  orderCancelIgnoredMessages,
} from '@/features/shop/services/Orders/constants'
import { isProduction } from '@/features/shared/utils/environment'
import { datadogRum } from '@datadog/browser-rum'
import { type MutationProduct } from '@shared/types'

type OrderAsKey = { order: Order }

export const useMutationSubmitItemFeedback = () => {
  return useMutation({
    mutationFn: (feedback: Feedback) =>
      apiPost<void>({
        config: {
          url: 'api/v1/customer_order_line_feedback.json',
          data: feedback,
        },
        options: {
          ignoredMessages: ['Feedback is past the allowed window'],
        },
        fetcherName: 'useMutationSubmitItemFeedback',
      }),
  })
}
export const useMutationTipOrder = () => {
  return useMutation({
    mutationFn: ({ orderId, tip }: { orderId: number; tip: Tip }) =>
      apiPut<Record<string, string>>({
        config: {
          url: `api/v1/orders/${orderId}/tip.json`,
          data: { tip: tip.value },
        },
        options: { ignoredMessages: ['Tipping window has closed'] },
        fetcherName: 'useMutationTipOrder',
      }),
    onSuccess: (_response, { orderId }, _result, context) =>
      context.client.invalidateQueries({
        queryKey: orderQueryOptions(orderId, 'marketplace').queryKey,
      }),
  })
}

export const useMutationCancelOrder = () => {
  const clearActiveOrder = useClearActiveOrder()
  const queryClient = useQueryClient()
  const currentActiveOrderId = useCurrentActiveOrderId()

  return useMutation({
    mutationFn: async (orderId: number) => {
      let reason_code = ''
      try {
        const reasonCodeData = await fetchReasonCode('cancel', 'API Request')
        reason_code = reasonCodeData[0]?.reason_code || ''
      } catch (e) {
        reason_code = ''
      }

      return apiPatch<void>({
        config: {
          url: `aviator/v2/orders/${orderId}/cancel.json`,
          params: { reason_code },
        },
        options: {
          ignoredMessages: orderCancelIgnoredMessages,
        },
        fetcherName: 'useMutationCancelOrder',
      })
    },
    onSuccess: (_, orderId) => {
      track({
        eventName: AnalyticsEvent.OrderCancelled,
        properties: { order_id: orderId },
      })
      queryClient.invalidateQueries({ queryKey: [ActiveOrdersQuery] })
      queryClient.invalidateQueries({ queryKey: [OrdersQuery] })
      queryClient.invalidateQueries({
        queryKey: orderQueryOptions(orderId, 'marketplace').queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: creditDetailsQueryOptions.queryKey,
      })
      // empties the current active order
      // state if the active order is the cancelled order
      if (currentActiveOrderId === orderId) {
        clearActiveOrder()
      }
    },
  })
}

export const useMutationRescheduleOrder = () => {
  const queryClient = useQueryClient()
  const storeParams = useStoreParams()

  return useMutation({
    mutationFn: async ({
      order,
      time_slot_id,
    }: OrderAsKey & {
      time_slot_id: string
      timezone: string
    }) => {
      let reason_code = ''
      try {
        const reasonCodeData = await fetchReasonCode('reschedule', 'Member Reschedule')
        reason_code = reasonCodeData[0]?.reason_code || ''
      } catch (e) {
        reason_code = isProduction ? 'RS-0117' : 'RS-0112'
      }

      return apiPatch<void>({
        config: {
          url: `aviator/v3/member/reschedule/${order.id}`,
          data: {
            delivery_window_id: time_slot_id,
            reason_code,
          },
        },
        fetcherName: 'useMutationRescheduleOrder',
      })
    },
    onSuccess: (_, { order }) =>
      queryClient.invalidateQueries({
        queryKey: orderQueryOptions(order.id, order.ingest_type).queryKey,
      }),
    onError: (
      error,
      {
        order: { customer_address, contains_alcohol = false, order_lines, order_type, id },
        timezone,
      },
    ) => {
      if (getErrorStatus(error) === 422) {
        queryClient.invalidateQueries({
          queryKey: timeslotsQueryOptions({
            address_id: customer_address?.id ?? 0,
            address_timezone: timezone,
            has_alcohol: contains_alcohol,
            is_pickup: order_type === 'pickup',
            item_count: order_lines.length,
            store_location_id: storeParams.store_location_id ?? 0,
            context: 'member-reschedule',
          }).queryKey,
        })
        trackElementClicked({
          display_sublocation: 'reschedule_order',
          content: 'error_updating_delivery_timeslot',
          type: 'button',
          order_id: String(id),
        })
      } else {
        trackElementClicked({
          display_sublocation: 'reschedule_order',
          content: 'error_with_reschedule',
          type: 'button',
          order_id: String(id),
        })
      }
    },
  })
}

export const useMutationRateOrder = () => {
  return useMutation({
    mutationFn: (rating: Rating) =>
      apiPost<Rating>({
        config: {
          url: 'aviator/v2/orders/ratings.json',
          data: rating,
        },
        fetcherName: 'useMutationRateOrder',
      }),
    /** OPTIMISTIC UPDATE */
    /** Since we are still using aviator/v2 for shopper ratings there is a risk that we could run into a race condition.
     * In order to remedy this after rating we need to Optimistically update the Order query cache  */
    onMutate: (rating, context) => {
      // Snapshot previous values for rollback
      const previousOrder = context.client.getQueryData(
        orderQueryOptions(rating.order_id, 'marketplace').queryKey,
      )

      // Optimistically update order
      if (previousOrder) {
        context.client.setQueryData(orderQueryOptions(rating.order_id, 'marketplace').queryKey, {
          ...previousOrder,
          shopper_rating: {
            comments: rating.comments,
            rateable: true,
            rating: rating.shopper_rating,
            tags: rating.tags,
          },
          shipt_rating: rating.shipt_rating,
        })
      }
      // Return context for rollback
      return { previousOrder }
    },

    /** ROLLBACK ON ERROR */
    onError: (_err, { order_id }, onMutateResult, context) => {
      if (onMutateResult?.previousOrder) {
        context.client.setQueryData(
          orderQueryOptions(order_id, 'marketplace').queryKey,
          onMutateResult.previousOrder,
        )
      }

      logError(new Error('There was an issue rating your shopper.'), {
        callLocation: 'orders/actions-rateOrder',
      })
    },
  })
}

const updateOrderIgnoredMessages = [
  'An order cannot be updated because shopping is in progress',
  'An order cannot be updated within one hour of the delivery window',
  'Can not remove all products from the order',
  /Sorry! You cannot add .* products to an order after it is placed/,
]

export const useMutationUpdateOrder = () => {
  const queryClient = useQueryClient()
  const storeParams = useStoreParams()

  return useMutation({
    mutationKey: [UpdateOrderMutation],
    mutationFn: ({
      order,
      products,
    }: OrderAsKey & { products: Record<string | number, MutationProduct> }) => {
      const payload = {
        ...storeParams,
        products: getOrderLinesForUpdateOrder(order, products).map((line) => {
          const { id, name, isCustomProduct, couponId } = line.requested_product
          return {
            ...(isCustomProduct ? { name } : { id }),
            ...(couponId && { coupon_id: couponId }),
            qty: line.requested_qty,
            note: line.notes,
          }
        }),
      }
      return apiPatch<AviatorOrder>({
        config: {
          url: `aviator/v2/orders/${order.id}/update.json`,
          data: payload,
        },
        options: {
          ignoredMessages: updateOrderIgnoredMessages,
        },
        fetcherName: 'useMutationUpdateOrder',
      })
    },
    onSuccess: (order, { products }) => {
      if (Object.values(products).some((p) => p.couponId)) {
        refreshCoupons()
      }
      return queryClient.invalidateQueries({
        queryKey: orderQueryOptions(order.id, order.ingest_type).queryKey,
      })
    },
  })
}

export const useMutationAddToCurrentOrder = () => {
  const queryClient = useQueryClient()
  const currentActiveOrderId = useCurrentActiveOrderId()

  return useMutation({
    mutationFn: (products: MutationProduct[]) => {
      const payload = {
        products: products.map((p) => ({
          ...(p.isCustomProduct ? { name: p.name } : { id: p.id }),
          ...(p.couponId && { coupon_id: p.couponId }),
          qty: getRequestedQuantityForProduct(p),
          note: p.note || '',
        })),
      }
      return apiPost<AviatorOrder>({
        config: {
          url: `aviator/v2/orders/${currentActiveOrderId}/add.json`,
          data: payload,
        },
        options: {
          ignoredMessages: updateOrderIgnoredMessages,
        },
        fetcherName: 'useMutationAddToCurrentOrder',
      })
    },
    onSuccess: (_, products) => {
      if (Object.values(products).some((p) => p.couponId)) {
        refreshCoupons()
      }
      if (currentActiveOrderId) {
        queryClient.invalidateQueries({
          queryKey: orderQueryOptions(currentActiveOrderId, 'marketplace').queryKey,
        })
      }
    },
    onSettled: (_, error, p) => {
      datadogRum.addAction('addToOrder', {
        isSuccess: !error,
        productCount: p.length,
        mutationType: 'add',
      })
    },
  })
}

export const useMutationRequestCallShopper = () => {
  return useMutation({
    mutationFn: ({ order_id, shopper_id }: { order_id: number; shopper_id: number }) =>
      apiPut<Record<string, string>>({
        config: {
          url: `in-app-chat/v1/chat_channel/${order_id}/${shopper_id}/request_callback`,
          headers: {
            'X-Request-Id': uuidv4(),
          },
        },
        fetcherName: 'useMutationRequestCallShopper',
      }),
  })
}

export const useMutationUpdateOrderMod = () => {
  return useMutation({
    mutationFn: ({ orderId, orderLineId, productId, action }: UpdateOrderModParams) =>
      apiPatch<UpdateOrderModResponse>({
        config: {
          url: `member-web-bff/v1/order/${orderId}/order-line/${orderLineId}/product/${productId}/${action}`,
        },
        options: {
          ignoredMessages: orderAlreadyProcessedIgnoredMessages,
        },
        fetcherName: 'useMutationUpdateOrderMod',
      }),
  })
}
